/**
 * OTP Input Component
 * 4-digit OTP input boxes
 * 
 * @author Varun Pandey
 */

import React, { useRef, useState, KeyboardEvent, ChangeEvent } from 'react';
import { cn } from '../../utils/cn';

export interface OTPInputProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  className?: string;
  inputClassName?: string;
  autoFocus?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 4,
  value: controlledValue,
  onChange,
  onComplete,
  className,
  inputClassName,
  autoFocus = false,
}) => {
  const [values, setValues] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input when component mounts
  React.useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 0);
    }
  }, [autoFocus]);

  const isControlled = controlledValue !== undefined;
  const currentValues = isControlled
    ? controlledValue.split('').concat(Array(length - controlledValue.length).fill(''))
    : values;

  const handleChange = (index: number, newValue: string) => {
    // Only allow digits
    const digit = newValue.replace(/\D/g, '').slice(-1);

    if (isControlled) {
      const nextValues = currentValues.slice(0, length);
      nextValues[index] = digit;
      const otpString = nextValues.join('');
      onChange?.(otpString);
      if (otpString.length === length && onComplete) {
        onComplete(otpString);
      }
    } else {
      const newValues = [...values];
      newValues[index] = digit;
      setValues(newValues);
      const otpString = newValues.join('');
      onChange?.(otpString);
      if (otpString.length === length && onComplete) {
        onComplete(otpString);
      }
    }

    // Auto-focus next input
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !currentValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    const pastedArray = pastedData.split('');

    if (isControlled) {
      onChange?.(pastedData);
      if (pastedData.length === length && onComplete) {
        onComplete(pastedData);
      }
    } else {
      const newValues = [...values];
      pastedArray.forEach((digit, i) => {
        if (i < length) {
          newValues[i] = digit;
        }
      });
      setValues(newValues);
      const otpString = newValues.join('');
      onChange?.(otpString);
      if (otpString.length === length && onComplete) {
        onComplete(otpString);
      }
    }

    // Focus the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  return (
    <div className={cn('flex gap-3 justify-center', className)}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={currentValues[index] || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={cn(
            'flex h-14 w-14 items-center justify-center rounded-[0.75rem] bg-black/90 border border-[#3A3A3C] text-center text-lg font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]',
            !currentValues[index] && 'text-[#8E8E93]',
            inputClassName
          )}
          placeholder="-"
        />
      ))}
    </div>
  );
};
